import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowLeft, Scissors, Phone, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, HAIR_CATEGORIES, IMAGES } from "../data/content";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollToTop";
import { useT } from "../i18n/LanguageContext";

function HairRow({ name, price, idx }) {
  const { tName, tPrice } = useT();
  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: idx * 0.03 }}
      className="flex items-baseline justify-between gap-4 py-3 border-b border-dashed border-white/10 last:border-b-0"
      data-testid={`hair-row-${idx}`}
    >
      <span className="text-white/85 text-sm sm:text-base font-light">{tName(name)}</span>
      <span className="text-[#D4AF37] font-serif-display text-lg sm:text-xl whitespace-nowrap">
        {tPrice(price)}
      </span>
    </motion.li>
  );
}

function HairPanel({ category, isOpen, onToggle }) {
  const { t } = useT();
  const title = t(`hair_cat.${category.id}.title`);
  const subtitle = t(`hair_cat.${category.id}.subtitle`);
  const badgeLabel = category.badge ? t(`hair_cat.${category.id}.badge`) : null;

  return (
    <div
      className={`card-lux lux-accordion ${isOpen ? "border-[rgba(212,175,55,0.45)]" : ""}`}
      data-state={isOpen ? "open" : "closed"}
      data-testid={`hair-category-${category.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-6 sm:p-8 flex items-center justify-between gap-6 group"
        data-testid={`hair-category-toggle-${category.id}`}
      >
        <div className="flex-1 min-w-0">
          {badgeLabel && (
            <span className="inline-block mb-3 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">
              {badgeLabel}
            </span>
          )}
          <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">{title}</h3>
          <p className="mt-2 text-sm text-white/50 tracking-wide">{subtitle}</p>
        </div>
        <div className="flex items-center justify-center w-12 h-12 rounded-full border border-white/15 text-[#D4AF37] flex-shrink-0 transition-all duration-300 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black">
          {isOpen ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 sm:px-8 pb-8">
              <ul className="space-y-0">
                {category.items.map((item, i) => (
                  <HairRow key={item.name} {...item} idx={i} />
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <a
                  href={`tel:${CONTACT.hairPhoneLink}`}
                  data-testid={`hair-book-${category.id}`}
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1 hover:border-[#D4AF37] transition-colors"
                >
                  <Phone size={12} strokeWidth={1.5} /> {t("friseur.book_for_call")} · {CONTACT.hairPhone}
                </a>
                <a
                  href={CONTACT.hairWhatsAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hair-whatsapp-${category.id}`}
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1 hover:border-[#D4AF37] transition-colors"
                >
                  <MessageCircle size={12} strokeWidth={1.5} /> {t("friseur.book_for_wa")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FriseurPage() {
  const [openId, setOpenId] = useState("damen");
  const { t } = useT();

  const cards = [
    { icon: null, title: t("friseur.card_1.title"), text: t("friseur.card_1.text") },
    { icon: Scissors, title: t("friseur.card_2.title"), text: t("friseur.card_2.text") },
    { icon: Phone, title: t("friseur.card_3.title"), text: t("friseur.card_3.text") },
  ];

  return (
    <div className="App relative" data-testid="friseur-page">
      <Navigation />

      <section
        id="top"
        data-testid="friseur-hero"
        className="relative min-h-[88vh] w-full overflow-hidden grain"
      >
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={IMAGES.hair_hero || IMAGES.aesthetic}
            alt="Friseur Studio — Hair & Beauty Nava"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-40 pb-20 min-h-[88vh] flex flex-col justify-end">
          <Link
            to="/"
            data-testid="friseur-back-link"
            className="mb-10 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> {t("friseur.back")}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Scissors size={14} className="text-[#D4AF37]" strokeWidth={1.5} />
              <span className="overline">{t("friseur.hero_overline")}</span>
            </div>

            <h1
              className="font-serif-display text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-white font-light tracking-tight"
              data-testid="friseur-title"
            >
              {t("friseur.hero_title_1")}
              <br />
              <em className="shimmer-gold not-italic font-normal">{t("friseur.hero_title_em")}</em>
            </h1>

            <p className="mt-8 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed font-light">
              {t("friseur.hero_subtitle")}{" "}
              <span className="text-[#D4AF37]">{t("friseur.hero_ladies")}</span>,{" "}
              <span className="text-[#D4AF37]">{t("friseur.hero_gents")}</span>{" "}
              {t("friseur.hero_and")}{" "}
              <span className="text-[#D4AF37]">{t("friseur.hero_kids")}</span>.
            </p>

            <div className="mt-12 flex flex-wrap gap-4">
              <a href={`tel:${CONTACT.hairPhoneLink}`} data-testid="friseur-hero-book-btn" className="btn-gold">
                <Phone size={16} strokeWidth={1.5} /> {t("friseur.hero_call")}
              </a>
              <a
                href={CONTACT.hairWhatsAppLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="friseur-hero-whatsapp-btn"
                className="btn-gold"
              >
                <MessageCircle size={16} strokeWidth={1.5} /> {t("friseur.hero_whatsapp")}
              </a>
              <a href="#leistungen-friseur" data-testid="friseur-hero-services-btn" className="btn-ghost-gold">
                {t("friseur.hero_prices")}
              </a>
            </div>

            <p className="mt-6 text-white/55 text-xs sm:text-sm tracking-wide">
              {t("friseur.hero_note")}{" "}
              <a href={`tel:${CONTACT.hairPhoneLink}`} className="text-[#D4AF37] hover:underline">
                {CONTACT.hairPhone}
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      <section
        id="leistungen-friseur"
        className="relative py-28 sm:py-40 bg-[#050505]"
        data-testid="friseur-services"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-16"
          >
            <span className="overline">{t("friseur.services_overline")}</span>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] font-light">
              {t("friseur.services_title_1")}{" "}
              <em className="text-[#D4AF37] not-italic">{t("friseur.services_title_em")}</em>
            </h2>
            <p className="mt-6 text-white/60 text-base sm:text-lg max-w-2xl font-light leading-relaxed">
              {t("friseur.services_description")}
            </p>
            <div className="mt-8 ornament justify-start">
              <div className="line" />
              <div className="diamond" />
              <div className="line" />
            </div>
          </motion.div>

          <div className="space-y-4 sm:space-y-5">
            {HAIR_CATEGORIES.map((cat) => (
              <HairPanel
                key={cat.id}
                category={cat}
                isOpen={openId === cat.id}
                onToggle={() => setOpenId(openId === cat.id ? "" : cat.id)}
              />
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {cards.map((item, i) => {
              const Icon = item.icon || Scissors;
              return (
                <div key={i} className="card-lux p-6">
                  <Icon className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
                  <p className="mt-4 font-serif-display text-xl text-white">{item.title}</p>
                  <p className="mt-2 text-sm text-white/55 font-light leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
